import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import RemotePhotosBanner from "@/components/RemotePhotosBanner";
import {
  PhotoImg, PhotoUploadProgress, authedFileUrl, downloadFile, FURNISH_STYLES,
  resolveFurnishStyle,
} from "@/components/propertyDetail/helpers";
import {
  UploadSimple, Sparkle, Trash, DownloadSimple, Image as ImageIcon,
  Spinner, Archive, ArrowRight, Check, ShareNetwork, X, CaretDown,
  Couch, PaintBrushBroad, ArrowsClockwise,
  Sliders, ShieldCheck, EyeSlash, Eye, Broom, Star, MagicWand,
  CaretLeft, CaretRight, ArrowLineUp, ArrowLineDown,
  ArrowsOutCardinal, Sun,
} from "@phosphor-icons/react";
import { usePropertyDetail } from "../context";
import { shareImage } from "@/lib/share";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import GenerationOverlay from "@/components/GenerationOverlay";
import SectionCoachmark from "@/components/SectionCoachmark";
import SortablePhotoGrid from "@/components/photo/SortablePhotoGrid";
import MiglioraTuttePointer from "@/components/photo/MiglioraTuttePointer";
import useEnhanceCredits from "@/hooks/useEnhanceCredits";

/**
 * FotoTab — gestione foto reali dell'immobile (upload, modalità Migliora,
 * batch enhance, galleria thumbnail). Estratto da PropertyDetail.jsx iter 247.
 *
 * Usa `usePropertyDetail()` per accedere allo stato del parent.
 */
export default function FotoTab() {
  const {
    id, property, photos, load, setTab,
    enhanceMode, setEnhanceMode, enhanceFoci, setEnhanceFoci,
    enhanceFocus, setEnhanceFocus,
    activePhotoId, setActivePhotoId,
    batchLoading, batchProgress, onBatchEnhance, onDownloadZip,
    onCancelBatch, cancellingBatch, activeBatchJobId,
    enhancingId, stagingId, restyleId, onEnhance, onStage, onRestyle, onDeletePhoto, onReorder,
    onEmptyRoom, emptyRoomId, emptyRoomHint, setEmptyRoomHint,
    uploading, uploadProgress, onUpload, onFilePick, fileInputRef,
    activePhoto, realPhotos,
    stageStyle, setStageStyle, stageRoom, setStageRoom,
    restyleStyle, setRestyleStyle, restyleChangeFloor, setRestyleChangeFloor,
    restyleFloorStyle, setRestyleFloorStyle,
  } = usePropertyDetail();
  // iter 313 r1 — multi-selezione stile FotoEditor (richiesta utente).
  // Niente più kebab "⋯" + checkmark verde. Click sulla thumb → toggle
  // selezione. La foto attiva (sx) è sempre l'ULTIMA cliccata in
  // selezione. Quando 0 foto selezionate, niente foto attiva.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // iter v26 r14 — selectMode esplicita (stesso pattern di Editor foto +
  // Galleria foto). Default tap = setActivePhotoId (foto grande a sx);
  // bottone "Seleziona" in toolbar → selectMode = check circle visibile,
  // tap = toggle selezione, sticky bar nera in basso con azioni.
  const [selectMode, setSelectMode] = useState(false);
  // bulkAction: null | "enhance" | "stage" | "restyle"
  const [bulkAction, setBulkAction] = useState(null);
  // iter v26 — Azione primaria scelta in alto ("Cosa faccio con le foto"):
  // enhance | stage | restyle. Determina cosa fa il bottone "primary"
  // nella bulk bar nera quando si selezionano le foto.
  const [bulkPrimary, setBulkPrimary] = useState("enhance");
  // Stile per Arreda/Cambia (usa FURNISH_STYLES, default casuale)
  const [bulkStyle, setBulkStyle] = useState("casuale");
  // Bulk runner state
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkDone, setBulkDone] = useState(false);
  const [bulkStep, setBulkStep] = useState("");
  const [bulkTotal, setBulkTotal] = useState(0);

  // iter v33 r29 · User: «Prezzo Chiaro sul Bottone: sul bottone Migliora
  // tutte mostra ≈ 8 crediti cosi sai in anticipo cosa spenderai». Hook
  // che espone il costo unitario di "photo_enhance" (default 1 credito),
  // e un flag `isUnlimited` per founder/beta (in quel caso il tag e' nascosto).
  const { cost: costPerEnhance, isUnlimited } = useEnhanceCredits("photo_enhance");

  // iter r95 · User feedback: «se una foto non mi piace deve poterla rifare
  // solo quella». Action sheet inline con 3 preset (Luminosa / Ultra-fedele /
  // Riordina meglio), agisce via POST /photos/{id}/regenerate sul SOLO
  // photo_id selezionato. Non tocca altre foto, non lancia batch.
  const [regenSheetOpen, setRegenSheetOpen] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const handleSingleRegenerate = async (preset, presetLabel) => {
    if (!activePhoto?.id || regenBusy) return;
    setRegenBusy(true);
    try {
      await api.post(`/photos/${activePhoto.id}/regenerate`, { preset });
      toast.success(`Foto rigenerata · ${presetLabel}`);
      setRegenSheetOpen(false);
      if (typeof load === "function") await load();
    } catch (e) {
      toast.error("Non sono riuscito a rigenerare la foto", { description: apiError(e) });
    } finally {
      setRegenBusy(false);
    }
  };

  // iter v33 r21 · User M1496 — Copertina automatica (AI photo ranking).
  const [autoCoverLoading, setAutoCoverLoading] = useState(false);
  const runAutoCover = async () => {
    if (autoCoverLoading || realPhotos.length < 2) return;
    setAutoCoverLoading(true);
    try {
      const { data } = await api.post(`/properties/${id}/photos/auto-cover`);
      const chosenId = data?.photo_id;
      const reason = data?.reason || "Migliore per composizione e luce";
      if (chosenId) {
        // Optimistic UI: sposta la foto scelta in cima
        const next = [...realPhotos];
        const srcIdx = next.findIndex((x) => x.id === chosenId);
        if (srcIdx > 0) {
          const [moved] = next.splice(srcIdx, 1);
          next.unshift(moved);
          if (typeof onReorder === "function") onReorder(next.map((x) => x.id));
        }
        toast.success("Copertina scelta da Pronto", { description: reason });
      } else {
        toast.info("Copertina invariata");
      }
    } catch (err) {
      console.error("[autoCover] failed", err?.response?.status, err?.response?.data);
      toast.error(apiError(err, "Non sono riuscito a scegliere la copertina"));
    } finally {
      setAutoCoverLoading(false);
    }
  };

  const selectedCount = selectedIds.size;
  const allRealIds = useMemo(() => realPhotos.map((p) => p.id), [realPhotos]);
  const allSelected = selectedCount > 0 && selectedCount === realPhotos.length;

  // iter v33 r27 · User: «Ordine Auto Intelligente: un tap Riordina per stanza
  // mette il soggiorno prima, poi cucina, camere, bagno — pronto per i portali».
  // Chiama /photos/smart-reorder che usa Gemini 3 Pro per identificare la
  // stanza di ogni foto e ordinare secondo la priorita' immobiliare.
  const [smartReorderLoading, setSmartReorderLoading] = useState(false);
  const runSmartReorder = async () => {
    if (smartReorderLoading || realPhotos.length < 3) return;
    setSmartReorderLoading(true);
    try {
      const { data } = await api.post(`/properties/${id}/photos/smart-reorder`);
      if (data?.reordered && Array.isArray(data.ordered_ids) && data.ordered_ids.length > 0) {
        // Optimistic UI: applica il nuovo ordine restituito dal server
        if (typeof onReorder === "function") onReorder(data.ordered_ids);
        // Compone un riepilogo tipo "soggiorno · esterno · cucina · camera · bagno"
        const counts = data.counts_by_room || {};
        const parts = [];
        const label = { living: "soggiorno", esterno: "esterno", cucina: "cucina", camera: "camere", bagno: "bagno", dettaglio: "dettagli", altro: "altro" };
        for (const k of ["living", "esterno", "cucina", "camera", "bagno", "dettaglio", "altro"]) {
          if (counts[k]) parts.push(`${counts[k]} ${label[k]}`);
        }
        toast.success("Riordinate per stanza", {
          description: parts.length > 0 ? parts.join(" · ") : "Ordine pronto per i portali",
        });
      } else {
        toast.info(data?.reason || "Ordine invariato");
      }
    } catch (err) {
      console.error("[smartReorder] failed", err?.response?.status);
      toast.error(apiError(err, "Riordino non riuscito · riprova tra un attimo"));
    } finally {
      setSmartReorderLoading(false);
    }
  };

  const toggleSelected = (pid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
    setActivePhotoId(pid);
  };
  const selectAll = () => setSelectedIds(new Set(allRealIds));
  const clearSelection = () => setSelectedIds(new Set());

  // iter v33 r22 · User M1497 — Riordino Bulk: sposta tutte le foto
  // selezionate come blocco (mantenendo l'ordine relativo tra loro).
  //   target="top"    → foto selezionate in cima (nuova copertina = prima delle selezionate)
  //   target="bottom" → foto selezionate in fondo alla galleria
  //   target=+1/-1    → sposta il blocco selezionato di 1 posizione avanti/indietro
  const selectedBlockBounds = useMemo(() => {
    if (selectedIds.size === 0) return { minIdx: -1, maxIdx: -1, atTop: true, atBottom: true };
    const idxs = realPhotos
      .map((p, i) => (selectedIds.has(p.id) ? i : -1))
      .filter((i) => i >= 0);
    if (idxs.length === 0) return { minIdx: -1, maxIdx: -1, atTop: true, atBottom: true };
    return {
      minIdx: idxs[0],
      maxIdx: idxs[idxs.length - 1],
      atTop: idxs[0] === 0,
      atBottom: idxs[idxs.length - 1] === realPhotos.length - 1,
    };
  }, [selectedIds, realPhotos]);

  const moveSelectedBlock = (target) => {
    if (selectedIds.size === 0) return;
    const selected = realPhotos.filter((p) => selectedIds.has(p.id));
    const others = realPhotos.filter((p) => !selectedIds.has(p.id));
    if (selected.length === 0) return;
    let next;
    if (target === "top") {
      if (selectedBlockBounds.atTop) return;
      next = [...selected, ...others];
    } else if (target === "bottom") {
      if (selectedBlockBounds.atBottom) return;
      next = [...others, ...selected];
    } else if (target === -1 || target === 1) {
      const { minIdx, maxIdx } = selectedBlockBounds;
      if (target === -1 && minIdx === 0) return;
      if (target === 1 && maxIdx === realPhotos.length - 1) return;
      const anchor = target === -1 ? minIdx - 1 : maxIdx + 1;
      const anchorPhoto = realPhotos[anchor];
      next = [];
      let inserted = false;
      for (const p of realPhotos) {
        if (target === -1 && p.id === anchorPhoto.id && !inserted) {
          next.push(...selected);
          inserted = true;
        }
        if (!selectedIds.has(p.id)) next.push(p);
        if (target === 1 && p.id === anchorPhoto.id && !inserted) {
          next.push(...selected);
          inserted = true;
        }
      }
    } else {
      return;
    }
    if (typeof onReorder === "function") {
      onReorder(next.map((x) => x.id));
      const n = selected.length;
      const msg = target === "top"
        ? `${n} ${n === 1 ? "foto spostata in cima" : "foto spostate in cima"}`
        : target === "bottom"
          ? `${n} ${n === 1 ? "foto spostata in fondo" : "foto spostate in fondo"}`
          : target === -1
            ? `${n} ${n === 1 ? "foto spostata indietro" : "foto spostate indietro"}`
            : `${n} ${n === 1 ? "foto spostata avanti" : "foto spostate avanti"}`;
      toast.success(msg);
    }
  };

  // iter v28 r39 — Click outside cancella selezione.
  // Se l'agente ha selezionato foto e clicca su un'area NON relativa
  // a foto/selezione/bulk-bar, automaticamente esce dal selectMode e
  // pulisce la selezione. Comodo quando ci si distrae e si clicca altro.
  useEffect(() => {
    if (!selectMode || selectedIds.size === 0) return;
    const onDocClick = (e) => {
      const root = e.target.closest(
        "[data-photo-select-root='1'], [data-bulk-bar='1'], [data-primary-action-card], button, input, textarea, label, [role='dialog']"
      );
      if (!root) {
        // Click in area "vuota" → cancella selezione e selectMode
        setSelectedIds(new Set());
        setSelectMode(false);
      }
    };
    // Usa "mousedown" per intercettare prima del click sui figli
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [selectMode, selectedIds.size]);

  /**
   * Esegue un'azione bulk (enhance/stage/restyle) sulle foto selezionate.
   * Mostra overlay con barra di progresso.
   */
  const runBulk = async (action) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkAction(null);
    setBulkRunning(true);
    setBulkDone(false);
    setBulkTotal(ids.length);
    setBulkStep(`Avvio su ${ids.length} ${ids.length === 1 ? "foto" : "foto"}…`);
    const targets = realPhotos.filter((p) => selectedIds.has(p.id));
    let done = 0, errors = 0;
    const CHUNK = 2;
    try {
      for (let i = 0; i < targets.length; i += CHUNK) {
        const slice = targets.slice(i, i + CHUNK);
        await Promise.allSettled(slice.map(async (p) => {
          try {
            if (action === "enhance") await onEnhance(p);
            else if (action === "stage") await onStage(p);
            else if (action === "restyle") await onRestyle(p);
            else if (action === "empty_room") await onEmptyRoom(p);
            done += 1;
          } catch (err) {
            errors += 1;
          }
        }));
        setBulkStep(`${done}/${targets.length} foto pronte${errors ? ` · ${errors} non riuscite` : ""}…`);
      }
      setBulkStep(errors === 0 ? "Tutte pronte!" : `${done} pronte · ${errors} non riuscite`);
      setBulkDone(true);
    } catch (err) {
      toast.error("Errore durante l'elaborazione");
      setBulkDone(true);
    } finally {
      setTimeout(() => {
        setBulkRunning(false);
        setBulkDone(false);
        setBulkStep("");
      }, 900);
    }
  };

  return (
    <>
      {/* iter v33 r8 · User M1485: «non è chiaro cosa uno può fare, sempre
          un po' macchinoso». Tutorial contestuale che appare UNA VOLTA
          per orientare l'agente nel tab Foto. Sostituisce il vecchio
          DismissibleIntro con un'ossatura 3-mosse più chiara. */}
      <SectionCoachmark
        id="property-foto-tab"
        title="Le foto in tre mosse"
        steps={[
          {
            icon: <UploadSimple size={15} weight="bold" />,
            title: "Carica dal telefono",
            body: "JPG, PNG o HEIC. Anche più foto insieme, dal sopralluogo o dal rullino.",
          },
          {
            icon: <Sparkle size={15} weight="bold" />,
            title: "Migliora tutte",
            body: "Un tap sistema luce, colori, prospettiva e riordino su tutte le foto in coda.",
          },
          {
            icon: <Couch size={15} weight="bold" />,
            title: "Arreda o svuota le singole",
            body: "Seleziona una foto e usa Arreda (vuota → mobili) o Cambia arredo (già arredata → altro stile).",
          },
        ]}
      />

      {/* Iter 240: banner foto remote in attesa di download (importate da gestionale) */}
      {(property?.remote_photo_urls?.length || 0) > 0 && (
        <RemotePhotosBanner
          pid={id}
          urls={property.remote_photo_urls}
          onDone={load}
        />
      )}

      {/* iter v28 r41 — Layout UNIFICATO con FotoEditor.jsx.
          Stessa struttura visiva: mode tabs compatti (4-col su desktop,
          ma qui 3 perché niente "Arreda pianta" in bulk), descrizione
          sotto, opzioni specifiche per modalità (segmented control per
          Migliora · chip per Stage/Restyle).
          Stessi colori, stesse classi, stessa esperienza. */}
      {photos.filter((p) => (p.photo_kind || "photo") === "photo").length > 0 && (
        <details open className="mt-3 mb-2 bg-[#F5F4F0] border border-[#E5E7E0] rounded-lg" data-testid="enhance-mode-bar">
          <summary className="px-3.5 py-3 list-none flex items-center justify-between gap-3 select-none rounded-lg [&::-webkit-details-marker]:hidden">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B]">
                Cosa faccio con le foto
              </div>
              <div className="text-[13px] text-[#1C1C1A] mt-0.5">
                <strong>
                  {bulkPrimary === "stage" ? "Arreda spazio vuoto"
                   : bulkPrimary === "restyle" ? "Cambia arredo"
                   : bulkPrimary === "empty_room" ? "Svuota stanza"
                   : "Migliora foto · automatico"}
                </strong>
                <span className="text-[#6B6A65] ml-1.5">·{" "}
                  {bulkPrimary === "stage" && "Mobili in stanze vuote"}
                  {bulkPrimary === "restyle" && "Stessa stanza, mobili nuovi"}
                  {bulkPrimary === "empty_room" && "Stanza arredata → vuota, per far vedere lo spazio"}
                  {bulkPrimary === "enhance" && "Luce, colori, prospettiva, riordino"}
                </span>
              </div>
            </div>
            {/* iter v33 r99 · User: «non voglio che possa scegliere la caratteristica.
                Migliora fa già tutto lui in automatico». Rimosso il chip "Scegli
                modalità" con caret perché faceva pensare che ci fossero opzioni
                di tuning nel Migliora. I 4 tab (Migliora/Arreda/Svuota/Cambia arredo)
                restano visibili sotto perché sono AZIONI diverse, non modalità. */}
          </summary>
          <div className="px-3 pb-3 pt-1 space-y-3">

          {/* Mode tabs — IDENTICI a FotoEditor.jsx (grid-cols compact button,
              dark bg attivo, F1F4ED bg inattivo, icon + label inline).
              iter v28 r390 — aggiunta "Svuota stanza" (empty_room) per
              parità con l'editor standalone /dashboard/foto. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" role="tablist" data-testid="foto-mode-tabs-card">
            {[
              { v: "enhance",    l: "Migliora",            icon: Sparkle },
              { v: "stage",      l: "Arreda spazio vuoto", icon: Couch },
              { v: "empty_room", l: "Svuota stanza",       icon: Broom },
              { v: "restyle",    l: "Cambia arredo",       icon: PaintBrushBroad },
            ].map(({ v, l, icon: Icon }) => {
              const active = bulkPrimary === v;
              return (
                <button
                  key={v}
                  onClick={() => setBulkPrimary(v)}
                  className={`py-2 px-2.5 text-[11.5px] font-medium rounded-lg inline-flex items-center justify-center gap-1.5 transition-all ${
                    active ? "bg-[#1C1C1A] text-[#FFFFFF]" : "bg-[#F1F4ED] text-[#1C1C1A] hover:bg-[#E5E7E0]"
                  }`}
                  data-testid={`primary-action-${v}`}
                  aria-pressed={active}
                >
                  <Icon size={13} weight={active ? "fill" : "regular"} />
                  <span className="truncate">{l}</span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-[#9A968B] px-1" data-testid="primary-action-desc">
            {bulkPrimary === "enhance" && "Luce, colori, ordine. Stessi mobili."}
            {bulkPrimary === "stage" && "Stanza vuota → arredata in stile."}
            {bulkPrimary === "empty_room" && "Stanza arredata → vuota. Mostra lo spazio reale senza i mobili attuali."}
            {bulkPrimary === "restyle" && "Stessa stanza, mobili nuovi."}
          </p>

          {/* ENHANCE: una sola modalità (Automatico).
              iter v28 r381 — User M1291: «togli "Scelgo io" e "Solo minime",
              lascia solo Automatico con scritto cosa fa. Tanto anche se
              seleziono una cosa sola me la modifica come l'automatico, e
              "Solo minime" non serve.» Sostituito il segmented control con
              una info-card che spiega cosa Pronto sistema in automatico. */}
          {bulkPrimary === "enhance" && (
            <div data-testid="bulk-enhance-options" className="space-y-2.5">
              {/* iter v33 r107 · Segmented compatto Completo | Solo luce.
                  Default Completo (comportamento invariato). Solo luce =
                  focus=keep_objects backend (preserva scena/arredi con
                  stessa qualità fotografica pro). Controlla sia Migliora
                  singola foto che Migliora tutte.
                  iter v33 r107b · User: nascondo Solo luce dalla UI
                  production. Il codice resta per riattivazione futura,
                  ma il segmented è visibile solo con
                  REACT_APP_SHOW_SOLO_LUCE=true. In produzione l'utente
                  usa solo Completo (default state invariato). */}
              {String(process.env.REACT_APP_SHOW_SOLO_LUCE || "").toLowerCase() === "true" && (
                <>
                  <div
                    role="tablist"
                    aria-label="Modalità Migliora"
                    className="inline-flex w-full sm:w-auto rounded-full bg-[#EAE7DF] p-0.5 gap-0"
                    data-testid="enhance-focus-picker"
                  >
                    {[
                      { v: "completo",  l: "Completo",  t: "completo"  },
                      { v: "solo_luce", l: "Solo luce", t: "solo-luce" },
                    ].map((opt) => {
                      const active = enhanceFocus === opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setEnhanceFocus(opt.v)}
                          className={`flex-1 sm:flex-none px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
                            active
                              ? "bg-[#1C1C1A] text-white shadow-sm"
                              : "text-[#6B6A65] hover:text-[#1C1C1A]"
                          }`}
                          data-testid={`enhance-focus-${opt.t}`}
                        >
                          {opt.l}
                        </button>
                      );
                    })}
                  </div>
                  <p
                    className="text-[11.5px] text-[#6B6A65] leading-snug px-1"
                    data-testid="enhance-focus-desc"
                  >
                    {enhanceFocus === "solo_luce"
                      ? "Interviene su luce, esposizione e colore."
                      : "Migliora luce, colori, prospettiva e piccoli disordini."}
                  </p>
                </>
              )}
              {/* Info-card verde originale (comportamento production
                  pre-r107): visibile quando il picker è nascosto */}
              {String(process.env.REACT_APP_SHOW_SOLO_LUCE || "").toLowerCase() !== "true" && (
                <div className="bg-[#F1F4ED] border border-[#7C8B6E]/25 rounded-xl p-3.5">
                  <div className="flex items-start gap-2.5">
                    <div className="shrink-0 w-7 h-7 rounded-full bg-[#7C8B6E] text-white inline-flex items-center justify-center">
                      <Sparkle size={13} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold text-[#1C1C1A] leading-tight">Pronto sistema tutto in automatico</div>
                      <div className="text-[11.5px] text-[#6B6A65] mt-1 leading-snug">
                        Bilancia luce ed esposizione, corregge i colori, raddrizza le linee storte,
                        aumenta la nitidezza e riordina i piccoli disordini (telecomandi, asciugamani fuori posto).
                        I mobili e gli arredi restano gli stessi.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SVUOTA STANZA: niente stile/pavimento, solo room hint opzionale.
              iter v28 r390 — User M1300: «manca Svuota stanza nell'editor
              dentro l'annuncio». Allinea con /dashboard/foto. */}
          {bulkPrimary === "empty_room" && (
            <div data-testid="bulk-empty-room-options" className="space-y-3">
              <div className="bg-[#F1F4ED] border border-[#7C8B6E]/25 rounded-xl p-3.5">
                <div className="flex items-start gap-2.5">
                  <div className="shrink-0 w-7 h-7 rounded-full bg-[#6B8E7F] text-white inline-flex items-center justify-center">
                    <Broom size={13} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#1C1C1A] leading-tight">Stanza arredata → vuota</div>
                    <div className="text-[11.5px] text-[#6B6A65] mt-1 leading-snug">
                      Mostra al compratore lo spazio reale senza i mobili attuali.
                      Architettura, finestre, porte e pavimento restano invariati.
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A65] mb-2">Tipo di stanza (opzionale)</div>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible">
                  {[
                    { v: "",                            l: "Auto" },
                    { v: "soggiorno",                   l: "Soggiorno" },
                    { v: "open space living cucina",    l: "Open space living-cucina" },
                    { v: "camera da letto",             l: "Camera" },
                    { v: "cucina",                      l: "Cucina" },
                    { v: "sala da pranzo",              l: "Sala da pranzo" },
                    { v: "studio",                      l: "Studio" },
                    { v: "cameretta bambini",           l: "Cameretta" },
                    { v: "bagno",                       l: "Bagno" },
                  ].map((r) => (
                    <button
                      key={r.v || "auto"}
                      onClick={() => setEmptyRoomHint(r.v)}
                      className={`shrink-0 px-3 py-1.5 text-xs rounded-full border transition-all ${emptyRoomHint === r.v ? "bg-[#6B8E7F] text-[#FFFFFF] border-[#6B8E7F]" : "bg-[#FFFFFF] text-[#1C1C1A] border-[#E5E7E0] hover:border-[#6B8E7F]"}`}
                      data-testid={`bulk-empty-room-hint-${r.v || "auto"}`}
                    >
                      {r.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="text-[10.5px] text-[#9A968B] italic px-1 leading-snug">
                Seleziona le foto qui sotto e premi <strong>Svuota stanza</strong> nella barra nera.
              </div>
            </div>
          )}

          {/* STAGE / RESTYLE: chip row IDENTICA a FotoEditor (room hints + style) */}
          {(bulkPrimary === "stage" || bulkPrimary === "restyle") && (
            <div className="space-y-4" data-testid="bulk-stage-options">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A65] mb-2">Diventerà...</div>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible">
                  {[
                    { v: "",                            l: "Casuale" },
                    { v: "soggiorno",                   l: "Soggiorno" },
                    { v: "open space living cucina",    l: "Open space living-cucina" },
                    { v: "camera da letto",             l: "Camera" },
                    { v: "cucina",                      l: "Cucina" },
                    { v: "sala da pranzo",              l: "Sala da pranzo" },
                    { v: "studio",                      l: "Studio" },
                    { v: "cameretta bambini",           l: "Cameretta" },
                    { v: "bagno",                       l: "Bagno" },
                  ].map((r) => (
                    <button
                      key={r.v || "auto"}
                      onClick={() => setStageRoom(r.v)}
                      className={`shrink-0 px-3 py-1.5 text-xs rounded-full border transition-all ${stageRoom === r.v ? "bg-[#7C8B6E] text-[#FFFFFF] border-[#7C8B6E]" : "bg-[#FFFFFF] text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                      data-testid={`bulk-room-${r.v || "auto"}`}
                    >
                      {r.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A65] mb-2">Stile arredo</div>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible">
                  {FURNISH_STYLES.map(({ v, l }) => (
                    <button
                      key={v}
                      onClick={() => setBulkStyle(v)}
                      className={`shrink-0 px-3.5 py-1.5 text-xs rounded-full border transition-all ${bulkStyle === v ? "bg-[#1C1C1A] text-[#FFFFFF] border-[#1C1C1A]" : "bg-[#FFFFFF] text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                      data-testid={`bulk-style-${v}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              {/* iter v28 r93 — Toggle "Cambia anche il pavimento" disponibile
                  sia per Cambia arredo che per Arreda spazio vuoto (richiesta
                  utente: "in arredo spazio vuoto mettimi che non cambia mai il
                  pavimento ma se metto la spunta cambia anche il pavimento").
                  Default OFF: il pavimento originale viene mantenuto. */}
              {(bulkPrimary === "restyle" || bulkPrimary === "stage") && (
                <div>
                  <label className="flex items-center gap-2 cursor-pointer text-[12.5px] text-[#1C1C1A]">
                    <input
                      type="checkbox"
                      checked={restyleChangeFloor}
                      onChange={(e) => setRestyleChangeFloor(e.target.checked)}
                      className="accent-[#7C8B6E]"
                      data-testid="bulk-floor-toggle"
                    />
                    <span>Cambia anche il <strong>pavimento</strong> <span className="text-[10.5px] text-[#9A968B]">(altrimenti mantiene l'originale)</span></span>
                  </label>
                  {restyleChangeFloor && (
                    <div className="mt-2 ml-6 flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">
                      {[
                        { v: "parquet_chiaro", l: "Parquet chiaro" },
                        { v: "parquet_scuro",  l: "Parquet scuro" },
                        { v: "gres_pietra",    l: "Gres effetto pietra" },
                        { v: "resina_chiara",  l: "Resina chiara" },
                        { v: "marmo",          l: "Marmo" },
                      ].map((f) => (
                        <button
                          key={f.v}
                          onClick={() => setRestyleFloorStyle(f.v)}
                          className={`shrink-0 px-3 py-1.5 text-[11px] rounded-full border transition-all ${restyleFloorStyle === f.v ? "bg-[#7C8B6E] text-white border-[#7C8B6E]" : "bg-white text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                          data-testid={`bulk-floor-${f.v}`}
                        >
                          {f.l}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="text-[10.5px] text-[#9A968B] italic px-1 leading-snug">
                Seleziona le foto qui sotto e premi <strong>{bulkPrimary === "stage" ? "Arreda spazio vuoto" : "Cambia arredo"}</strong> nella barra nera.
              </div>
            </div>
          )}
          </div>
        </details>
      )}

      {/* iter 313 r1 — Hero bar dinamica:
            • 0 selezionate → mostra "N foto totali · Migliora tutte" come prima
            • 1+ selezionate → la barra azioni bulk diventa STICKY in fondo
              (vedi <FixedBulkBar /> sotto) così appare vicino alla galleria
              su mobile, niente più duplicazione con "Cosa faccio con le foto"
          iter v33 r29 · Aggiunto anche il fumetto onboarding "Premi Migliora
          tutte per iniziare" al primo accesso (localStorage flag one-shot). */}
      {photos.length > 0 && selectedCount === 0 && (() => {
        const real = photos.filter((p) => (p.photo_kind || "photo") === "photo");
        const pending = real.filter((p) => !p.enhanced_path).length;
        const done = real.length - pending;
        if (real.length === 0) return null;
        // Fumetto onboarding: mostrato solo se ci sono foto da migliorare
        // e l'agente non ha mai migliorato nulla su nessun annuncio.
        const showPointer = pending > 0 && done === 0;
        return (
          <>
          <div className="mt-4 mb-2 px-3 py-2.5 bg-[#7C8B6E]/8 border border-[#7C8B6E]/20 rounded-full inline-flex items-center gap-2.5 w-full max-w-full" data-testid="batch-hero">
            <Sparkle size={14} weight="fill" className="text-[#7C8B6E] shrink-0" />
            <div className="flex-1 min-w-0 text-[12.5px] leading-tight truncate">
              {pending > 0 ? (
                <span className="text-[#1C1C1A]">
                  <strong className="text-[#7C8B6E]">{pending}</strong> {pending === 1 ? "foto da migliorare" : "foto da migliorare"}
                  {done > 0 && <span className="text-[#6B6A65]"> · {done} fatte</span>}
                  <span className="text-[#6B6A65] hidden sm:inline"> · oppure seleziona singole foto</span>
                </span>
              ) : (
                <span className="text-[#6B6A65]">
                  <strong className="text-[#1C1C1A]">Tutte le foto migliorate</strong>
                  <span className="hidden sm:inline"> · seleziona le foto per arredarle o cambiare l'arredo</span>
                </span>
              )}
            </div>
            <div className="shrink-0 flex items-center gap-2">
              {pending > 0 && !isUnlimited && (
                <span
                  className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#6B6A65] tabular-nums"
                  data-testid="batch-enhance-cost"
                  title={`Ogni foto costa ${costPerEnhance} credito. Totale stimato: ${pending * costPerEnhance} crediti.`}
                >
                  ≈ {pending * costPerEnhance} cr
                </span>
              )}
              {pending > 0 ? (
                batchLoading && activeBatchJobId ? (
                  <button
                    onClick={onCancelBatch}
                    disabled={cancellingBatch}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#D14D4D] text-[#D14D4D] hover:bg-[#FFEEEE] text-[12px] font-semibold disabled:opacity-60 transition-colors"
                    data-testid="batch-cancel-hero"
                    title="Ferma Migliora tutte · le foto gia' migliorate restano"
                  >
                    {cancellingBatch
                      ? <><Spinner size={11} className="animate-spin" /> Annullo…</>
                      : <><X size={11} weight="bold" /> Annulla</>}
                  </button>
                ) : (
                  <button
                    onClick={onBatchEnhance}
                    disabled={batchLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[12px] font-semibold disabled:opacity-60 transition-colors"
                    data-testid="batch-enhance-hero"
                  >
                    {batchLoading
                      ? <><Spinner size={11} className="animate-spin" /> {batchProgress ? `${batchProgress.done}/${batchProgress.total}` : "…"}</>
                      : <><Sparkle size={11} weight="fill" /> Migliora tutte</>}
                  </button>
                )
              ) : (
                <button
                  onClick={() => onDownloadZip("enhanced")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1C1C1A] hover:bg-black text-white text-[12px] font-semibold transition-colors"
                  data-testid="batch-zip-hero"
                >
                  <Archive size={11} weight="bold" /> ZIP
                </button>
              )}
            </div>
          </div>
          <MiglioraTuttePointer show={showPointer} />
          </>
        );
      })()}

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-8">
        {/* iter v28 r47 — Strip foto selezionate: quando l'agente ha 1+
            foto selezionate e torna su tramite "Riprova", deve VEDERE
            le foto su cui applicherà l'azione. Prima mostrava solo
            l'activePhoto (1 sola) → confusione "ho selezionato 3 ma
            ne vedo 1". Ora strip orizzontale con thumb tappabile. */}
        {selectedCount > 0 && (() => {
          // iter v28 r86 — Conteggio per stato: native vs migliorate.
          const selectedPhotos = realPhotos.filter((p) => selectedIds.has(p.id));
          const enhancedCount = selectedPhotos.filter((p) => p.enhanced_path).length;
          const nativeCount = selectedCount - enhancedCount;
          let summary;
          if (enhancedCount === 0) summary = `applico a tutte`;
          else if (nativeCount === 0) summary = `rigenero tutte`;
          else summary = `${nativeCount} da migliorare · ${enhancedCount} da rigenerare`;
          return (
          <div className="mb-3 p-3 rounded-lg bg-[#7C8B6E]/8 border border-[#7C8B6E]/25" data-testid="selected-strip">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E]">
                <strong>{selectedCount}</strong> {selectedCount === 1 ? "foto selezionata" : "foto selezionate"} · {summary}
              </div>
              <button
                type="button"
                onClick={clearSelection}
                className="text-[11px] text-[#6B6A65] hover:text-[#1C1C1A] underline underline-offset-2"
                data-testid="selected-strip-clear"
              >
                Annulla
              </button>
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
              {selectedPhotos.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setActivePhotoId(p.id)}
                  className={`relative flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                    activePhoto?.id === p.id
                      ? "border-[#7C8B6E] ring-2 ring-[#7C8B6E]/30"
                      : "border-[#EAE7DF] hover:border-[#7C8B6E]"
                  }`}
                  style={{ width: 64, height: 64 }}
                  data-testid={`selected-strip-${p.id}`}
                  title={p.room_type || "Foto"}
                >
                  <PhotoImg
                    src={authedFileUrl(p.enhanced_path || p.original_path, false, 160)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {p.enhanced_path && (
                    <span className="absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full bg-[#7C8B6E] flex items-center justify-center" title="Già migliorata">
                      <Check size={7} weight="bold" className="text-white" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
          );
        })()}
        {activePhoto ? (
          // iter v28 r83 — "After" preferenziale: staged_path (Cambia arredo
          // / Arreda) > enhanced_path (Migliora). Prima mostravamo SOLO
          // enhanced_path: dopo Cambia arredo l'utente non vedeva mai il
          // risultato nel viewport principale (era in un piccolo pannello
          // sotto) → "sembra che non sia cambiato niente".
          (activePhoto.staged_path || activePhoto.enhanced_path) ? (
            <div className="relative">
              <BeforeAfterSlider
                beforeSrc={authedFileUrl(activePhoto.original_path)}
                afterSrc={authedFileUrl(activePhoto.staged_path || activePhoto.enhanced_path)}
                height="h-[62vh]"
                fit="contain"
              />
              {activePhoto.staged_path && (
                <span className="absolute top-3 left-3 z-30 bg-[#1C1C1A]/90 text-white text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full">
                  {activePhoto.staged_style?.startsWith("restyle") ? "Arredo cambiato" : "Arredata"}
                </span>
              )}
              {/* iter v28 r86 — X in alto a destra: chiude l'editor grande
                  (deseleziona la foto attiva). NON elimina la foto. */}
              <button
                onClick={() => setActivePhotoId(null)}
                aria-label="Chiudi anteprima"
                title="Chiudi anteprima"
                className="absolute top-3 right-3 z-30 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-md text-[#1C1C1A] transition-all"
                data-testid="close-active-photo-btn"
              >
                <X size={15} weight="bold" />
              </button>
              {/* r95j-hotfix2 (feb 2026) · User: «non voglio vedere Più
                  luminosa / Ultra-fedele / Riordina meglio, lasciamo solo
                  Rigenera». Rimosso il pulsante flottante "Rigenera questa
                  foto" + action sheet inline con 3 preset. L'unica azione
                  di rigenerazione resta il bottone nero "Rigenera" in
                  fondo al pannello, che chiama onEnhance con la modalità
                  attualmente selezionata (default = completo).
                  Lo state `regenSheetOpen` + handler `handleSingleRegenerate`
                  restano nel codice come dead code temporaneo (non lo tocco
                  in questo giro per minimizzare il perimetro). */}
            </div>
          ) : (
            <div className="card-flat relative h-[62vh] overflow-hidden flex items-center justify-center bg-[#1C1C1A]" data-testid="active-photo-original">
              <PhotoImg
                src={authedFileUrl(activePhoto.original_path)}
                alt=""
                className="w-full h-full object-contain"
                fallbackLabel="Foto originale non disponibile · ricaricala"
                onBrokenClick={onFilePick}
                /* iter v32 r52 — User M1463: «non mi carica la foto in grande».
                   Su PROD l'object storage non sempre serve CORS headers; con
                   crossOrigin=anonymous il browser scarta l'immagine in
                   silenzio (panel bianco senza fallback). Disabilitato qui
                   detectUniform: la foto in grande appare anche senza CORS. */
                detectUniform={false}
              />
              <span className="absolute top-4 left-4 overline bg-[#FFFFFF]/85 px-3 py-1">Originale</span>
              {/* iter v28 r86 — X in alto a destra anche sulla foto originale. */}
              <button
                onClick={() => setActivePhotoId(null)}
                aria-label="Chiudi anteprima"
                title="Chiudi anteprima"
                className="absolute top-3 right-3 z-30 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-md text-[#1C1C1A] transition-all"
                data-testid="close-active-photo-btn"
              >
                <X size={15} weight="bold" />
              </button>
            </div>
          )
        ) : (
          <div
            onClick={onFilePick}
            className="card-flat h-[62vh] flex flex-col items-center justify-center text-center cursor-pointer hover:border-[#7C8B6E] transition-colors"
            data-testid="empty-photo-zone"
          >
            <UploadSimple size={40} weight="light" className="text-[#7C8B6E]" />
            <h3 className="font-editorial text-3xl mt-4">Carica le foto del sopralluogo</h3>
            <p className="mt-2 text-[#6B6A65] max-w-xs">Scatta o scegli dalla galleria · JPG, PNG, HEIC · anche più foto insieme</p>
            <div className="mt-6 btn-primary"><UploadSimple size={16} weight="bold" /> Scatta o seleziona</div>
          </div>
        )}

        {activePhoto && (
          <div className="mt-3 space-y-2" data-testid="active-photo-actions">
            {/* iter 298: Scarica/Rimuovi SOPRA il bottone Migliora — l'utente
                segnalava che da desktop venivano nascoste sotto il bottone
                full-width "Rigenera" e dovevano scrollare per trovarle.
                iter 2026-02: single-slot invariant. "Scarica foto" e
                "Condividi" puntano SEMPRE all'unica versione corrente:
                enhanced_path (se presente) → staged_path (se presente) →
                original_path. L'utente non deve pensare in "versioni". */}
            {(() => {
              const currentEditedPath =
                activePhoto.enhanced_path
                || activePhoto.staged_path
                || activePhoto.original_path;
              return (
              <div className="flex items-center gap-2 flex-wrap pb-1">
                <button
                  onClick={() => downloadFile(currentEditedPath, `pronto-foto-${activePhoto.id}.jpg`)}
                  className="inline-flex items-center gap-1.5 text-[12.5px] text-[#1C1C1A] hover:text-[#7C8B6E] font-semibold bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] px-3 py-1.5 rounded-full transition-colors"
                  data-testid="download-enhanced-btn"
                >
                  <DownloadSimple size={13} weight="bold" /> Scarica foto
                </button>
                {/* iter 309 r17: parità desktop/mobile — Condividi sempre visibile. */}
                <button
                  onClick={async () => {
                    try {
                      await shareImage({
                        url: authedFileUrl(currentEditedPath),
                        filename: `pronto-foto-${activePhoto.id}.jpg`,
                        title: property?.title || "Foto immobile",
                        text: "",
                      });
                    } catch { toast.error("Condivisione non riuscita"); }
                  }}
                  className="inline-flex items-center gap-1.5 text-[12.5px] text-[#1C1C1A] hover:text-[#7C8B6E] font-semibold bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] px-3 py-1.5 rounded-full transition-colors"
                  data-testid="share-active-photo-btn"
                >
                  <ShareNetwork size={13} weight="bold" /> Condividi
                </button>
                <button
                  onClick={async () => {
                    const newState = !activePhoto.portal_excluded;
                    try {
                      await api.patch(`/photos/${activePhoto.id}`, {
                        portal_excluded: newState,
                      });
                      toast.success(newState ? "Esclusa dai portali" : "Inclusa sui portali");
                      await load();
                    } catch (e) {
                      toast.error("Errore: " + (e?.response?.data?.detail || e.message || "riprova"));
                    }
                  }}
                  className={`inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-full transition-colors ${
                    activePhoto.portal_excluded
                      ? "bg-[#D14D4D] text-white border border-[#D14D4D] hover:bg-[#C03A3A]"
                      : "bg-white text-[#1C1C1A] border border-[#E5E7E0] hover:border-[#7C8B6E] hover:text-[#7C8B6E]"
                  }`}
                  data-testid="toggle-portal-excluded-btn"
                  title={activePhoto.portal_excluded
                    ? "Foto esclusa dai pacchetti per Idealista/Immobiliare · clicca per includerla"
                    : "Escludi questa foto dai pacchetti per i portali (resta in galleria)"}
                >
                  {activePhoto.portal_excluded
                    ? <><EyeSlash size={13} weight="fill" /> Non sui portali</>
                    : <><EyeSlash size={13} weight="bold" /> Escludi dai portali</>}
                </button>
                <button
                  onClick={() => onDeletePhoto(activePhoto)}
                  className="inline-flex items-center gap-1.5 text-[12.5px] text-[#9A968B] hover:text-red-600 hover:border-red-300 font-semibold bg-white border border-[#E5E7E0] px-3 py-1.5 rounded-full ml-auto transition-colors"
                  data-testid="delete-photo-btn"
                  title="Elimina la foto dall'annuncio (azione definitiva)"
                >
                  <Trash size={13} weight="bold" /> Elimina dall'annuncio
                </button>
              </div>
              );
            })()}

            {/* iter v28 r83 — Il bottone PRIMARIO sulla foto attiva ora
                rispetta `bulkPrimary` (Migliora / Arreda / Cambia arredo)
                scelto nel pannello "Cosa faccio con le foto". Prima chiamava
                SEMPRE onEnhance: l'utente configurava "Cambia arredo" +
                "Cucina" ma il bottone "Rigenera" lanciava enhance → il
                cambio arredo non avveniva mai. */}
            <button
              onClick={() => {
                if (bulkPrimary === "restyle") {
                  onRestyle(activePhoto, resolveFurnishStyle(bulkStyle), stageRoom, restyleChangeFloor, restyleFloorStyle);
                } else if (bulkPrimary === "stage") {
                  onStage(activePhoto, resolveFurnishStyle(bulkStyle), stageRoom, restyleChangeFloor, restyleFloorStyle);
                } else if (bulkPrimary === "empty_room") {
                  onEmptyRoom(activePhoto, emptyRoomHint);
                } else {
                  onEnhance(activePhoto);
                }
              }}
              disabled={enhancingId === activePhoto.id || stagingId === activePhoto.id || restyleId === activePhoto.id || emptyRoomId === activePhoto.id}
              className="w-full bg-[#1C1C1A] hover:bg-black text-white py-2.5 px-3 rounded-full text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
              data-testid="enhance-btn"
            >
              {(() => {
                const busy = enhancingId === activePhoto.id || stagingId === activePhoto.id || restyleId === activePhoto.id || emptyRoomId === activePhoto.id;
                if (busy && bulkPrimary === "enhance") return <><Spinner size={15} className="animate-spin" /> Sto migliorando…</>;
                if (busy && bulkPrimary === "stage")   return <><Spinner size={15} className="animate-spin" /> Sto arredando…</>;
                if (busy && bulkPrimary === "restyle") return <><Spinner size={15} className="animate-spin" /> Sto cambiando l'arredo…</>;
                if (busy && bulkPrimary === "empty_room") return <><Spinner size={15} className="animate-spin" /> Sto svuotando la stanza…</>;
                if (bulkPrimary === "stage")   return <><Couch size={15} weight="fill" /> Arreda spazio vuoto</>;
                if (bulkPrimary === "restyle") return <><PaintBrushBroad size={15} weight="fill" /> Cambia arredo</>;
                if (bulkPrimary === "empty_room") return <><Broom size={15} weight="fill" /> Svuota stanza</>;
                return activePhoto.enhanced_path
                  ? <><Sparkle size={15} weight="fill" /> Rigenera</>
                  : <><Sparkle size={15} weight="fill" /> Migliora</>;
              })()}
            </button>
            {/* iter v28 r86 — Hint render: se la foto è marcata come render/CGI
                o se after enhance vediamo che è un'immagine sintetica,
                informiamo l'agente che "Migliora" fa solo micro-aggiustamenti
                su un'immagine già pulita. Lo invitiamo a usare "Cambia arredo". */}
            {(activePhoto.is_render || activePhoto.photo_kind === "render" || activePhoto.is_synthetic) && (
              <div className="mt-1.5 p-2.5 rounded-md bg-[#FFF8E6] border border-[#F2D27A] text-[11.5px] text-[#7A5C13] leading-snug flex items-start gap-2" data-testid="render-hint">
                <ImageIcon size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
                <div>
                  <strong>È un render</strong> · "Migliora" fa solo micro-aggiustamenti perché
                  l'immagine è già pulita. Per modifiche visibili (riarredare, cambiare destinazione
                  d'uso, ecc.) usa il pannello <strong>"Cosa faccio con queste foto"</strong> più in basso
                  e scegli <strong>Cambia arredo</strong>.
                </div>
              </div>
            )}

            {/* iter v33 r16 · Retry Selettivo — Hint chiaro quando la foto
                attiva ha già fallito 2+ volte l'auto-enhance. L'utente
                capisce perché è ancora "originale" e sa cosa fare. */}
            {!activePhoto.enhanced_path && (activePhoto.enhance_fail_count || 0) >= 2 && (
              <div
                className="mt-1.5 p-2.5 rounded-md bg-[#FCEDED] border border-[#E9B8B8] text-[11.5px] text-[#8A2A2A] leading-snug flex items-start gap-2"
                data-testid="enhance-failed-hint"
              >
                <X size={14} weight="bold" className="flex-shrink-0 mt-0.5" />
                <div>
                  {/* iter r95d · A2 · traduzione errori tecnici → user friendly.
                      Timeout e rate_limit sono transitori, non veri fallimenti:
                      guidiamo l'utente a riprovare invece che a eliminare la foto.
                      Altri errori (no_image, fidelity_blocked, ecc.) mostrano il
                      messaggio originale + codice, coerente col comportamento
                      pre-r95d. */}
                  {(() => {
                    const err = String(activePhoto.enhance_last_error || "").toLowerCase();
                    if (err === "timeout") {
                      return (
                        <>
                          <strong>Foto pesante</strong> · L'AI ci sta mettendo più tempo del solito.
                          Riprova tra qualche minuto o premi <strong>Migliora</strong> qui sopra per rifarla adesso.
                        </>
                      );
                    }
                    if (err.includes("rate") || err.includes("429") || err.includes("quota")) {
                      return (
                        <>
                          <strong>Troppe richieste</strong> · Aspetta 30 secondi e premi
                          {" "}<strong>Migliora</strong> qui sopra per riprovare.
                        </>
                      );
                    }
                    if (err === "fidelity_blocked" || err === "identity_altered") {
                      return (
                        <>
                          <strong>L'AI ha alterato la stanza</strong> · Il controllo di identità
                          ha rilevato che l'AI ha modificato elementi reali (sanitari, cucina,
                          mobili o architettura). L'originale è intatto. Premi
                          {" "}<strong>Migliora</strong> qui sopra per riprovare.
                        </>
                      );
                    }
                    return (
                      <>
                        <strong>Auto-miglioria non riuscita</strong> · Ho provato due volte senza esito
                        {activePhoto.enhance_last_error ? ` (${activePhoto.enhance_last_error})` : ""}.
                        Prova a premere <strong>Migliora</strong> qui sopra per rifarla manualmente,
                        oppure elimina questa foto e caricane una simile.
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Modifiche avanzate (arreda, rinnova, rimuovi oggetti) ──
            Spostate negli Strumenti → Foto. Qui dentro l'annuncio resta
            SOLO il bottone Migliora per pulire le foto reali.
            Lasciamo solo: card "Stanza arredata" se già processata e link
            rapido a Foto Editor con la foto pre-caricata. */}

        {activePhoto?.staged_path && (
          <div className="mt-4 p-4 rounded-md bg-[#7C8B6E]/8 border border-[#7C8B6E]/30 flex items-center gap-3 flex-wrap" data-testid="staged-panel">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-bold">Stanza arredata · stile {activePhoto.staged_style || "moderno"}</div>
              <p className="text-[12.5px] text-[#1C1C1A] mt-0.5">La stanza è già stata arredata. Puoi scaricarla o rifarla dagli Strumenti.</p>
            </div>
            <button
              onClick={() => downloadFile(
                activePhoto.enhanced_path || activePhoto.staged_path || activePhoto.original_path,
                `pronto-foto-${activePhoto.id}.jpg`,
              )}
              className="btn-outline text-sm"
              data-testid="download-staged-btn"
            >
              <DownloadSimple size={14} /> Scarica
            </button>
          </div>
        )}

        {/* iter v28 r178 — micro-link Studio Visivo · soft upsell.
            Appare solo dopo che l'agente ha fatto almeno 1 "Arreda" o
            "Cambia arredo" su questa foto (cioè ha già toccato il tasto
            staged): segnale che sta lavorando su un annuncio "ambizioso".
            Linka in fondo a Materiali, dove sta la card completa. */}
        {(activePhoto?.staged_path || activePhoto?.restyle_path) && (
          <button
            type="button"
            onClick={() => setTab("file")}
            className="mt-3 w-full text-left p-3.5 rounded-md bg-gradient-to-r from-[#1C1C1A] to-[#2A2A26] hover:from-[#2A2A26] hover:to-[#3A3A36] text-white transition-colors flex items-center justify-between gap-3 min-h-[44px]"
            data-testid="fototab-studio-visivo-link"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Sparkle size={14} weight="fill" className="text-[#C8A66B] shrink-0" />
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold leading-tight">
                  Per un annuncio premium serve qualcosa di più curato?
                </div>
                <div className="text-[11px] text-white/65 mt-0.5 leading-snug">
                  Render fotorealistici e virtual staging su misura · studio visivo su richiesta
                </div>
              </div>
            </div>
            <ArrowRight size={13} weight="bold" className="text-white/70 shrink-0" />
          </button>
        )}
      </div>

      {/* Gallery sidebar */}
      <aside className="lg:col-span-4">
        <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#9A968B]">
            {realPhotos.length} {realPhotos.length === 1 ? "foto" : "foto"}
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* iter v33 r21 · User M1496 — Bottone "Copertina auto":
                Pronto sceglie con AI la foto migliore da mettere per prima.
                Mostrato solo con 2+ foto (con una sola non serve). */}
            {realPhotos.length >= 2 && !selectMode && (
              <button
                onClick={runAutoCover}
                disabled={autoCoverLoading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11.5px] font-semibold transition-colors bg-white border border-[#C9B27A] text-[#8A7745] hover:bg-[#FBF3E0] disabled:opacity-60 disabled:cursor-wait"
                data-testid="phototab-auto-cover-btn"
                title="Pronto sceglie la foto migliore da mettere in copertina"
              >
                {autoCoverLoading ? (
                  <><Spinner size={12} weight="bold" className="animate-spin" /> Scelgo…</>
                ) : (
                  <><MagicWand size={12} weight="fill" /> Copertina auto</>
                )}
              </button>
            )}
            {/* iter v33 r27 · User: «Ordine Auto Intelligente: un tap
                "Riordina per stanza" mette il soggiorno prima, poi cucina,
                camere, bagno — pronto per i portali». */}
            {realPhotos.length >= 3 && !selectMode && (
              <button
                onClick={runSmartReorder}
                disabled={smartReorderLoading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11.5px] font-semibold transition-colors bg-white border border-[#7C8B6E] text-[#5B6B4E] hover:bg-[#F1F4ED] disabled:opacity-60 disabled:cursor-wait"
                data-testid="phototab-smart-reorder-btn"
                title="Ordino soggiorno · esterno · cucina · camere · bagno · dettagli"
              >
                {smartReorderLoading ? (
                  <><Spinner size={12} weight="bold" className="animate-spin" /> Riordino…</>
                ) : (
                  <><ArrowsClockwise size={12} weight="bold" /> Riordina per stanza</>
                )}
              </button>
            )}
            {/* iter v26 r14 — Bottone Seleziona (stesso pattern Editor foto) */}
            {realPhotos.length > 0 && (
              <button
                onClick={() => {
                  if (selectMode) {
                    setSelectMode(false);
                    setSelectedIds(new Set());
                  } else {
                    setSelectMode(true);
                  }
                }}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11.5px] font-semibold transition-colors ${
                  selectMode
                    ? "bg-[#1C1C1A] text-white hover:bg-black"
                    : "bg-white border border-[#E5E7E0] text-[#1C1C1A] hover:border-[#7C8B6E] hover:bg-[#F1F4ED]"
                }`}
                data-testid="phototab-toggle-select"
              >
                <Check size={12} weight="bold" /> {selectMode ? "Annulla" : "Seleziona"}
              </button>
            )}
            <Link
              to={`/dashboard/galleria?import=${id}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white hover:bg-[#F1F4ED] text-[#1C1C1A] border border-[#E5E7E0] hover:border-[#7C8B6E] text-[11.5px] font-semibold transition-colors"
              data-testid="add-from-gallery-btn"
              title="Aggiungi foto già lavorate dalla Galleria Pronto"
            >
              <ImageIcon size={12} weight="fill" /> Galleria Pronto
            </Link>
            <button
              onClick={onFilePick}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[11.5px] font-semibold shadow-sm transition-colors"
              data-testid="add-more-photos-btn"
              title="Carica foto dal tuo dispositivo (file, fotocamera, rullino)"
            >
              <UploadSimple size={12} weight="bold" /> Carica da file
            </button>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="photo-file-input" />
        {uploading && <PhotoUploadProgress p={uploadProgress} />}
        {/* iter v33 r26 · Riordino con tap-and-hold su mobile + drag su
            desktop tramite @dnd-kit. Silenzioso (no toast). Le frecce
            grandi sotto ogni thumb sono state rimosse.
            iter r61 · User: «l'iconcina in alto a destra non e' chiara,
            non si capisce cosa fa». Icona 4-frecce (affordance universale
            "sposta") + hint riscritto in modo diretto. Nessuna modifica
            al motore drag: soglie tocco invariate. */}
        {realPhotos.length > 0 && !selectMode && (
          <div className="mb-2 text-[12px] leading-snug text-[#6B6A65] px-0.5" data-testid="reorder-hint">
            La prima foto è la <strong className="font-semibold text-[#1C1C1A]">copertina</strong>. Per cambiarla tieni premuto il pallino bianco <ArrowsOutCardinal size={11} weight="bold" className="inline align-[-1px]" /> in alto a destra sulla foto e trascinala al primo posto.
          </div>
        )}
        <SortablePhotoGrid
          items={realPhotos.map((p) => p.id)}
          disabled={selectMode}
          onReorder={(newOrder) => {
            if (typeof onReorder === "function") onReorder(newOrder);
          }}
          className="grid grid-cols-3 gap-2.5"
          gridTestid="property-photo-gallery"
          renderItem={(pid, photoIdx, handle) => {
            const p = realPhotos.find((x) => x.id === pid);
            if (!p) return null;
            const isActive = activePhotoId === p.id;
            const isSelected = selectedIds.has(p.id);
            const isCover = photoIdx === 0;
            return (
              <div
                ref={handle.setNodeRef}
                {...handle.attributes}
                onClick={() => {
                  // iter v26 r14 — Default tap = setActivePhotoId (foto grande
                  // a sinistra). In selectMode tap = toggle selezione.
                  // Stesso pattern Editor foto.
                  // iter v33 r48 · I listener del drag NON sono piu' qui:
                  // solo l'handle .drag-handle sotto puo' avviare il drag.
                  // Cosi' il tocco/scroll sulla foto e' sempre libero.
                  if (selectMode) toggleSelected(p.id);
                  else setActivePhotoId(p.id);
                }}
                style={handle.style}
                className={`group relative aspect-square overflow-hidden rounded-lg bg-[#F1F4ED] border cursor-pointer transition-all ${
                  isSelected
                    ? "border-[#7C8B6E] shadow-[0_6px_18px_-6px_rgba(124,139,110,0.55)]"
                    : isActive
                      ? "border-[#7C8B6E]/50"
                      : "border-[#EAE7DF] hover:border-[#7C8B6E] active:scale-[0.98]"
                }`}
                data-testid={`thumb-${p.id}`}
                data-selected={isSelected ? "true" : "false"}
                data-cover={isCover ? "true" : "false"}
                data-photo-select-root="1"
              >
                <PhotoImg
                  src={authedFileUrl(p.enhanced_path || p.original_path, false, 480)}
                  alt=""
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                    isSelected ? "" : "group-hover:scale-105"
                  }`}
                  fallbackLabel="Persa"
                  detectUniform={true}
                />
                {selectMode && (
                  <span
                    className={`absolute top-2 left-2 w-5 h-5 aspect-square shrink-0 rounded-full inline-flex items-center justify-center transition-all pointer-events-none z-10 ${
                      isSelected
                        ? "bg-[#7C8B6E] border-2 border-white shadow-md"
                        : "bg-white/85 border-2 border-white/95 shadow-sm group-hover:bg-white"
                    }`}
                    style={{ minWidth: "20px", minHeight: "20px", width: "20px", height: "20px" }}
                    aria-label={isSelected ? "Selezionata" : "Seleziona"}
                    data-testid={`thumb-${p.id}-select`}
                  >
                    {isSelected && <Check size={11} weight="bold" className="text-white" />}
                  </span>
                )}
                {isSelected && (
                  <span
                    className="absolute inset-0 bg-[#7C8B6E]/15 pointer-events-none"
                    aria-hidden="true"
                  />
                )}
                {p.portal_excluded && (
                  <span
                    className="absolute top-2 right-2 z-10 bg-[#D14D4D] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 pointer-events-none shadow-sm"
                    data-testid={`thumb-${p.id}-portal-excluded`}
                    title="Esclusa dai pacchetti per i portali"
                  >
                    <EyeSlash size={9} weight="fill" /> Non sui portali
                  </span>
                )}
                {p.portal_excluded && (
                  <span
                    className="absolute inset-0 bg-[#1C1C1A]/35 pointer-events-none"
                    aria-hidden="true"
                  />
                )}
                {p.enhanced_path && (
                  <span className="absolute bottom-1.5 right-1.5 bg-[#7C8B6E] text-white text-[9.5px] px-1.5 py-0.5 inline-flex items-center gap-1 rounded font-semibold pointer-events-none">
                    <Sparkle size={9} weight="fill" /> Pronto
                  </span>
                )}
                {!p.enhanced_path && (p.enhance_fail_count || 0) >= 2 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      // iter v33 r27 · User: «Riprova Foto Non Riuscite: sul
                      // badge rosso Non riuscita aggiungi un tap che rilancia
                      // solo quella foto senza consumare doppi crediti».
                      // stopPropagation cosi' non attiva il tap-per-selezionare.
                      e.stopPropagation();
                      if (selectMode) return;
                      // Rilancia l'enhance sulla singola foto. `_enhance_one`
                      // backend resetta fail_count in caso di successo.
                      if (typeof onEnhance === "function") onEnhance(p);
                    }}
                    disabled={enhancingId === p.id || selectMode}
                    className="absolute bottom-1.5 right-1.5 bg-[#D14D4D] hover:bg-[#B84040] active:bg-[#A03535] text-white text-[9.5px] px-1.5 py-0.5 inline-flex items-center gap-1 rounded font-semibold shadow-sm disabled:opacity-70 transition-colors cursor-pointer z-10"
                    data-testid={`thumb-${p.id}-enhance-failed`}
                    title="Tocca per riprovare a migliorare questa foto"
                  >
                    {enhancingId === p.id
                      ? <><Spinner size={9} className="animate-spin" /> Riprovo…</>
                      : <><ArrowsClockwise size={9} weight="bold" /> Riprova</>}
                  </button>
                )}
                {p.room_state === "empty" && p.room_state_confidence >= 0.85 && !p.staged_path && (
                  // iter v33 r47 · User: «Il discorso vuota non e' chiaro,
                  // uno magari non capisce che e' una stanza vuota».
                  // Sostituito il badge passivo "VUOTA" con una pill
                  // ATTIVA "Arreda con AI" cliccabile: tap → apre la foto
                  // nel lightbox dove c'e' gia' il pulsante Arreda spazio
                  // vuoto pronto. Cosi' comunichi il valore (puoi
                  // arredare virtualmente questa stanza) invece di una
                  // pura etichetta descrittiva.
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePhotoId(p.id);
                    }}
                    className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 bg-[#7C8B6E] hover:bg-[#6E7B62] text-white text-[9.5px] px-2 py-0.5 uppercase tracking-wider rounded font-bold shadow-sm transition-colors"
                    title="Stanza vuota — tocca per arredarla virtualmente"
                    data-testid={`thumb-${p.id}-arreda-btn`}
                  >
                    <Couch size={10} weight="fill" /> Arreda
                  </button>
                )}
                {!selectMode && !p.portal_excluded && isCover && (
                  <span
                    className="absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-1 bg-[#C9B27A] text-[#1C1C1A] text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded pointer-events-none shadow-sm"
                    data-testid={`thumb-${p.id}-cover-badge`}
                    title="Foto di copertina — visibile su vetrina, brochure, portali"
                  >
                    <Star size={9} weight="fill" /> Copertina
                  </span>
                )}
                {/* iter v33 r54 · Utente: «il pulsantino per riordinare
                    ingombra troppo ma deve essere piu' chiaro». Sostituito
                    il bottone scuro traslucido (poco leggibile su foto
                    scure) con una piccola pill bianca stile iOS Notes con
                    ombra sottile: 18×18px, icona nera antracite, contrasto
                    forte su qualsiasi foto senza rubare spazio visivo.
                    Il tocco resta ben preciso (22px area di tap grazie
                    al padding) → riordinare con il dito e' facile.
                    iter r61 · L'icona a 6 puntini non era chiara. Passiamo
                    all'icona 4-frecce (ArrowsOutCardinal): affordance
                    universale "muovi in ogni direzione". Handle a 22×22
                    con icona 13 per aumentare leggibilita' senza cambiare
                    il layout della griglia. */}
                {!selectMode && (
                  <button
                    type="button"
                    {...handle.listeners}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="absolute top-1 right-1 z-10 w-[22px] h-[22px] p-[4px] flex items-center justify-center bg-white/90 hover:bg-white active:bg-white text-[#1C1C1A] rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.18)] active:scale-95 transition-all cursor-grab active:cursor-grabbing"
                    style={{ touchAction: "none", WebkitBackdropFilter: "blur(4px)", backdropFilter: "blur(4px)" }}
                    aria-label="Tieni premuto e trascina per spostare la foto"
                    title="Tieni premuto e trascina per spostare la foto. La prima è la copertina."
                    data-testid={`thumb-${p.id}-drag-handle`}
                  >
                    <ArrowsOutCardinal size={13} weight="bold" />
                  </button>
                )}
              </div>
            );
          }}
        />
        {realPhotos.length === 0 && (
          <div className="grid grid-cols-3 gap-2.5">
            <div className="col-span-3 text-sm text-[#6B6A65] text-center py-8">
              Nessuna foto ancora. Clicca su &quot;Carica da file&quot; per aggiungere.
            </div>
          </div>
        )}
      </aside>
      </div>

      {/* iter 313 r1 — Modal unificato per le 3 azioni bulk (enhance/stage/restyle).
          L'utente sceglie i parametri e applica a TUTTE le foto selezionate.
          Backend chiamato in chunk di 2 con barra di progresso (GenerationOverlay). */}
      {bulkAction && (
        <BatchActionModal
          action={bulkAction}
          count={selectedCount}
          onClose={() => setBulkAction(null)}
          onConfirm={() => {
            // iter v26 — propaga la scelta in alto (bulkStyle) al campo
            // specifico del modal prima di lanciare il batch.
            // iter v28 r83 — "casuale" pesca UNO stile concreto random (era
            // forzato a "moderno": l'utente sceglieva casuale ma riceveva
            // sempre moderno, quindi sembrava che l'AI non cambiasse nulla
            // se la stanza era già moderna).
            if (bulkAction === "stage" && bulkStyle) setStageStyle(resolveFurnishStyle(bulkStyle));
            if (bulkAction === "restyle" && bulkStyle) setRestyleStyle(resolveFurnishStyle(bulkStyle));
            runBulk(bulkAction);
          }}
          enhanceMode={enhanceMode} setEnhanceMode={setEnhanceMode}
          enhanceFoci={enhanceFoci} setEnhanceFoci={setEnhanceFoci}
          stageStyle={bulkAction === "stage" && bulkStyle !== "casuale" ? bulkStyle : stageStyle} setStageStyle={setStageStyle}
          stageRoom={stageRoom} setStageRoom={setStageRoom}
          restyleStyle={bulkAction === "restyle" && bulkStyle !== "casuale" ? bulkStyle : restyleStyle} setRestyleStyle={setRestyleStyle}
          restyleChangeFloor={restyleChangeFloor} setRestyleChangeFloor={setRestyleChangeFloor}
          restyleFloorStyle={restyleFloorStyle} setRestyleFloorStyle={setRestyleFloorStyle}
        />
      )}

      {/* Overlay barra di progresso durante l'elaborazione */}
      <GenerationOverlay
        open={bulkRunning}
        done={bulkDone}
        estimateMs={Math.max(8000, bulkTotal * 6000)}
        title={
          bulkAction === "stage" ? "Sto arredando le stanze…"
          : bulkAction === "restyle" ? "Sto cambiando l'arredo…"
          : bulkAction === "empty_room" ? "Sto svuotando le stanze…"
          : "Sto migliorando le foto…"
        }
        step={bulkStep}
        hint={bulkTotal > 0 ? `${bulkTotal} ${bulkTotal === 1 ? "foto" : "foto"} in lavorazione` : ""}
      />

      {/* iter v26 r3 — STICKY BULK BAR.
          User feedback (02/2026): la vecchia bulk bar inline sopra alla
          galleria duplicava visivamente la card "Cosa faccio con le foto"
          e su mobile risultava troppo lontana dalle thumbnail: l'utente
          selezionava una foto in fondo, e doveva scrollare su per vedere le
          azioni. Ora: barra fissa in basso (sopra alla bottom-bar del browser
          mobile via safe-area-inset), appare solo quando c'è selezione.
          z-50 per stare sopra alle thumbnail, shadow per stacco visivo. */}
      {selectedCount > 0 && (
        <>
          {/* Spacer: evita che la sticky bar copra l'ultima riga di
              thumbnail / azioni in fondo alla pagina su mobile */}
          <div aria-hidden="true" className="h-24" data-testid="bulk-bar-spacer" />
          <div
            className="fixed left-2 right-2 z-50 px-3 py-2.5 bg-[#1C1C1A] text-white rounded-2xl shadow-2xl flex items-center gap-2 flex-wrap animate-in slide-in-from-bottom-4 duration-200"
            style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
            data-testid="bulk-bar"
            data-bulk-bar="1"
          >
          <span className="text-[12.5px] font-semibold flex-shrink-0 inline-flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-[#7C8B6E] inline-flex items-center justify-center text-[10px] font-bold">
              {selectedCount}
            </span>
            <span className="hidden sm:inline">{selectedCount === 1 ? "foto selezionata" : "foto selezionate"}</span>
            <span className="sm:hidden">{selectedCount === 1 ? "selezionata" : "selezionate"}</span>
          </span>
          <button
            type="button"
            onClick={allSelected ? clearSelection : selectAll}
            className="text-[11.5px] text-white/75 hover:text-white underline underline-offset-2 transition-colors"
            data-testid="bulk-toggle-all"
          >
            {allSelected ? "Deseleziona" : `Tutte (${realPhotos.length})`}
          </button>
          <div className="flex-1 hidden sm:block" />
          <div className="flex items-center gap-1.5 ml-auto flex-wrap justify-end">
            {/* iter v26 r4 — User feedback: i 3 simboli Migliora/Arreda/Cambia
                non sono chiari nella sticky bar. Sostituiti da UN SOLO bottone
                "Riprova" che riporta l'utente in alto al pannello "Cosa
                faccio con le foto" (l'immagine grande + le opzioni visibili),
                così sceglie l'azione dalla card principale.
                iter v26 r5 — rinominato da "Rifai impostazioni" a "Riprova"
                per coerenza con il bottone Riprova dell'editor foto. */}
            <button
              type="button"
              onClick={() => {
                // Riporta l'utente al pannello di scelta in alto
                const panel = document.querySelector('[data-testid="enhance-mode-bar"]');
                if (panel) {
                  // Apre il <details> se chiuso
                  if (panel.tagName === 'DETAILS' && !panel.open) panel.open = true;
                  panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[12px] font-semibold transition-colors"
              data-testid="bulk-rifai"
              title="Torna su per cambiare impostazioni e riapplicare"
            >
              <ArrowsClockwise size={12} weight="bold" /> Riprova
            </button>

            {/* iter v33 r22 · User M1497 — Riordino Bulk: 4 bottoni per
                spostare tutte le foto selezionate come blocco (In cima /
                In fondo per grandi jump; frecce ⬅/➡ per fine-tune di 1
                posizione). Preserva l'ordine relativo tra le foto
                selezionate. Bottoni si disabilitano quando il blocco e'
                gia' al bordo (top/bottom). */}
            <div
              className="inline-flex items-center gap-0.5 rounded-full bg-white/10 p-0.5"
              data-testid="bulk-reorder-group"
            >
              <button
                type="button"
                onClick={() => moveSelectedBlock("top")}
                disabled={selectedBlockBounds.atTop}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent text-white text-[11.5px] font-semibold transition-colors"
                data-testid="bulk-move-top"
                title="Sposta le foto selezionate in cima"
              >
                <ArrowLineUp size={12} weight="bold" /> In cima
              </button>
              <button
                type="button"
                onClick={() => moveSelectedBlock(-1)}
                disabled={selectedBlockBounds.atTop}
                className="w-7 h-7 rounded-full hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent text-white flex items-center justify-center transition-colors"
                data-testid="bulk-move-back"
                aria-label="Sposta il blocco indietro"
                title="Sposta il blocco indietro"
              >
                <CaretLeft size={12} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => moveSelectedBlock(1)}
                disabled={selectedBlockBounds.atBottom}
                className="w-7 h-7 rounded-full hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent text-white flex items-center justify-center transition-colors"
                data-testid="bulk-move-forward"
                aria-label="Sposta il blocco avanti"
                title="Sposta il blocco avanti"
              >
                <CaretRight size={12} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => moveSelectedBlock("bottom")}
                disabled={selectedBlockBounds.atBottom}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent text-white text-[11.5px] font-semibold transition-colors"
                data-testid="bulk-move-bottom"
                title="Sposta le foto selezionate in fondo"
              >
                <ArrowLineDown size={12} weight="bold" /> In fondo
              </button>
            </div>

            <button
              type="button"
              onClick={async () => {
                const targets = realPhotos.filter((p) => selectedIds.has(p.id));
                if (targets.length === 0) return;
                const anyIncluded = targets.some((p) => !p.portal_excluded);
                const target = anyIncluded;
                try {
                  await api.post(`/properties/${id}/photos/portal-exclude`, {
                    photo_ids: targets.map((p) => p.id),
                    portal_excluded: target,
                  });
                  toast.success(
                    target
                      ? `${targets.length} ${targets.length === 1 ? "foto esclusa" : "foto escluse"} dai portali`
                      : `${targets.length} ${targets.length === 1 ? "foto inclusa" : "foto incluse"} sui portali`
                  );
                  clearSelection();
                  await load();
                } catch (e) {
                  toast.error("Errore: " + (e?.response?.data?.detail || e.message || "riprova"));
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-[12px] font-semibold transition-colors"
              data-testid="bulk-portal-toggle"
              title="Escludi/includi le foto selezionate nei pacchetti per i portali immobiliari"
            >
              {(() => {
                const targets = realPhotos.filter((p) => selectedIds.has(p.id));
                const anyIncluded = targets.some((p) => !p.portal_excluded);
                return anyIncluded ? (
                  <><EyeSlash size={12} weight="bold" /> Escludi dai portali</>
                ) : (
                  <><Eye size={12} weight="bold" /> Includi sui portali</>
                );
              })()}
            </button>
            <button
              type="button"
              onClick={async () => {
                const targets = realPhotos.filter((p) => selectedIds.has(p.id));
                for (const p of targets) {
                  const name = `pronto-${(property?.title || "foto").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${p.id.slice(-6)}.jpg`;
                  await downloadFile(p.enhanced_path || p.staged_path || p.original_path, name);
                }
                clearSelection();
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-[12px] font-semibold transition-colors"
              data-testid="bulk-download"
              title="Scarica le foto selezionate"
            >
              <DownloadSimple size={12} weight="bold" /> Scarica
            </button>
            <button
              type="button"
              onClick={async () => {
                const targets = realPhotos.filter((p) => selectedIds.has(p.id));
                const n = targets.length;
                const ok = await new Promise((resolve) => {
                  let decided = false;
                  const decide = (v) => { if (!decided) { decided = true; resolve(v); } };
                  toast.warning(
                    `Eliminare ${n} ${n === 1 ? "foto" : "foto"}? Non è reversibile.`,
                    {
                      duration: 10000,
                      action: { label: "Elimina", onClick: () => decide(true) },
                      cancel: { label: "Annulla", onClick: () => decide(false) },
                      onDismiss: () => decide(false),
                      onAutoClose: () => decide(false),
                    }
                  );
                });
                if (!ok) return;
                const results = await Promise.allSettled(
                  targets.map((p) => onDeletePhoto(p.id, { silent: true }))
                );
                const failed = results.filter((r) => r.status === "rejected").length;
                clearSelection();
                if (failed === 0) {
                  toast.success(`${n} ${n === 1 ? "foto eliminata" : "foto eliminate"}`);
                } else {
                  toast.error(`${n - failed} eliminate · ${failed} non riuscite`);
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600/80 hover:bg-red-600 text-white text-[12px] font-semibold transition-colors"
              data-testid="bulk-delete"
              title="Elimina le foto selezionate"
            >
              <Trash size={12} weight="bold" /> Elimina
            </button>
          </div>
          </div>
        </>
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// BatchActionModal — UI unificata per scegliere parametri prima di
// applicare un'azione (Migliora / Arreda spazio vuoto / Cambia arredo)
// alle foto selezionate.
// ──────────────────────────────────────────────────────────────────────
function BatchActionModal({
  action, count, onClose, onConfirm,
  enhanceMode, setEnhanceMode, enhanceFoci, setEnhanceFoci,
  stageStyle, setStageStyle, stageRoom, setStageRoom,
  restyleStyle, setRestyleStyle, restyleChangeFloor, setRestyleChangeFloor,
  restyleFloorStyle, setRestyleFloorStyle,
}) {
  const isEnhance = action === "enhance";
  const isStage = action === "stage";
  const isRestyle = action === "restyle";

  const STYLES = [
    { v: "moderno",       l: "Moderno",       sub: "Pulito, minimal" },
    { v: "scandinavo",    l: "Scandinavo",    sub: "Bianco, legno chiaro" },
    { v: "classico",      l: "Classico",      sub: "Calmo, raffinato" },
    { v: "elegante",      l: "Elegante",      sub: "Lussuoso, ricercato" },   /* iter v28 r39 */
    { v: "industriale",   l: "Industriale",   sub: "Metallo, mattoni" },
    { v: "boho",          l: "Boho",          sub: "Caldo, tessuti" },
    { v: "mediterraneo",  l: "Mediterraneo",  sub: "Bianco, blu, naturale" },
  ];

  const FLOOR_STYLES = [
    { v: "parquet_chiaro",  l: "Parquet chiaro" },
    { v: "parquet_scuro",   l: "Parquet scuro" },
    { v: "marmo",           l: "Marmo" },
    { v: "gres_chiaro",     l: "Gres chiaro" },
    /* iter v28 r39 — rimosso "cemento" */
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 flex items-end sm:items-center justify-center sm:p-6"
      onClick={onClose}
      data-testid="bulk-action-modal-backdrop"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90dvh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-action-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2 border-b border-[#E5E7E0]">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B]">
              {count} {count === 1 ? "foto selezionata" : "foto selezionate"}
            </div>
            <h3 className="font-editorial text-xl text-[#1C1C1A] mt-0.5">
              {isEnhance ? "Migliora le foto" : isStage ? "Arreda lo spazio vuoto" : "Cambia arredo"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-[#6B6A65] hover:text-[#1C1C1A] -mt-1 -mr-1 w-8 h-8 inline-flex items-center justify-center"
            aria-label="Chiudi"
            data-testid="bulk-action-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {isEnhance && (
            <div className="bg-[#F1F4ED] border border-[#7C8B6E]/25 rounded-xl p-3.5" data-testid="bulk-enhance-info">
              <div className="flex items-start gap-2.5">
                <div className="shrink-0 w-7 h-7 rounded-full bg-[#7C8B6E] text-white inline-flex items-center justify-center">
                  <Sparkle size={13} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-[#1C1C1A] leading-tight">Pronto sistema tutto in automatico</div>
                  <div className="text-[11.5px] text-[#6B6A65] mt-1 leading-snug">
                    Bilancia luce ed esposizione, corregge i colori, raddrizza le linee storte,
                    aumenta la nitidezza e riordina i piccoli disordini. I mobili restano gli stessi.
                  </div>
                </div>
              </div>
            </div>
          )}

          {isStage && (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2">Stile dell'arredo</div>
                <div className="grid grid-cols-2 gap-2">
                  {STYLES.map(({ v, l, sub }) => {
                    const a = stageStyle === v;
                    return (
                      <button
                        key={v}
                        onClick={() => setStageStyle(v)}
                        className={`text-left rounded-lg px-2.5 py-2 transition-all border ${a ? "bg-[#1C1C1A] text-white border-[#1C1C1A]" : "bg-white text-[#3F3D38] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                        data-testid={`bulk-stage-style-${v}`}
                      >
                        <div className="text-[12.5px] font-semibold leading-tight">{l}</div>
                        <div className={`text-[10.5px] mt-0.5 ${a ? "text-white/70" : "text-[#6B6A65]"}`}>{sub}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1.5">Tipo di stanza <span className="text-[#6B6A65] font-medium normal-case tracking-normal">(opzionale · lascia vuoto e Pronto decide)</span></div>
                <input
                  type="text"
                  value={stageRoom}
                  onChange={(e) => setStageRoom(e.target.value)}
                  placeholder="es. soggiorno, camera matrimoniale, cucina abitabile…"
                  className="w-full px-3 py-2 rounded-lg border border-[#E5E7E0] focus:border-[#7C8B6E] focus:ring-1 focus:ring-[#7C8B6E]/30 text-[13px] outline-none transition-all"
                  data-testid="bulk-stage-room"
                />
              </div>
              <div className="text-[11.5px] text-[#6B6A65] leading-snug bg-[#FAF9F4] border border-[#E5E7E0] p-2.5 rounded-lg">
                Le foto vengono arredate <strong>dalla nativa</strong>: muri, pavimenti e finestre restano identici.
              </div>
            </>
          )}

          {isRestyle && (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2">Nuovo stile</div>
                <div className="grid grid-cols-2 gap-2">
                  {STYLES.map(({ v, l, sub }) => {
                    const a = restyleStyle === v;
                    return (
                      <button
                        key={v}
                        onClick={() => setRestyleStyle(v)}
                        className={`text-left rounded-lg px-2.5 py-2 transition-all border ${a ? "bg-[#1C1C1A] text-white border-[#1C1C1A]" : "bg-white text-[#3F3D38] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                        data-testid={`bulk-restyle-style-${v}`}
                      >
                        <div className="text-[12.5px] font-semibold leading-tight">{l}</div>
                        <div className={`text-[10.5px] mt-0.5 ${a ? "text-white/70" : "text-[#6B6A65]"}`}>{sub}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="bulk-restyle-floor-toggle">
                <input
                  type="checkbox"
                  checked={restyleChangeFloor}
                  onChange={(e) => setRestyleChangeFloor(e.target.checked)}
                  className="w-4 h-4 accent-[#7C8B6E]"
                />
                <span className="text-[13px] text-[#1C1C1A]">Cambia anche il pavimento</span>
              </label>
              {restyleChangeFloor && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2">Pavimento</div>
                  <div className="flex flex-wrap gap-1.5">
                    {FLOOR_STYLES.map(({ v, l }) => {
                      const a = restyleFloorStyle === v;
                      return (
                        <button
                          key={v}
                          onClick={() => setRestyleFloorStyle(v)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11.5px] font-medium transition-all ${a ? "bg-[#7C8B6E] text-white border border-[#7C8B6E]" : "bg-white text-[#3F3D38] border border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                          data-testid={`bulk-restyle-floor-${v}`}
                        >
                          {l}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="text-[11.5px] text-[#6B6A65] leading-snug bg-[#FAF9F4] border border-[#E5E7E0] p-2.5 rounded-lg">
                Sostituisco l'arredo esistente mantenendo <strong>la stanza identica</strong>. Riparto dalla foto originale.
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-[#E5E7E0] flex items-center justify-between gap-3 bg-white">
          <button onClick={onClose} className="text-[13px] text-[#6B6A65] hover:text-[#1C1C1A] font-medium" data-testid="bulk-action-cancel">
            Annulla
          </button>
          <button
            onClick={onConfirm}
            className="bg-[#1C1C1A] hover:bg-black text-white px-5 py-2.5 rounded-full text-[13px] font-semibold inline-flex items-center gap-2"
            data-testid="bulk-action-confirm"
          >
            {isEnhance ? <Sparkle size={14} weight="fill" /> : isStage ? <Couch size={14} weight="fill" /> : <PaintBrushBroad size={14} weight="fill" />}
            {isEnhance ? `Migliora ${count} ${count === 1 ? "foto" : "foto"}`
              : isStage ? `Arreda ${count} ${count === 1 ? "foto" : "foto"}`
              : `Rinnova ${count} ${count === 1 ? "foto" : "foto"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
